import { createContext, type ReactNode, useContext, useMemo } from "react";

/**
 * The shell's slot for a page-level nav rail, rendered by `Layout` between
 * the app sidebar and the page column. A page fills it by portal, so the
 * rail is a sibling of the sidebar in the DOM while staying the page's own
 * component.
 *
 * `slot` is `null` until `Layout` has committed: on a full page load the
 * shell and the page mount together, so the element does not exist yet
 * during the page's first render.
 */
type State = {
  slot: HTMLElement | null;
};

const PageNavSlotContext = createContext<State | null>(null);

type Props = {
  slot: HTMLElement | null;
  children: ReactNode;
};

export const PageNavSlotProvider: React.FC<Props> = ({ slot, children }) => {
  const value = useMemo(() => ({ slot }), [slot]);

  return (
    <PageNavSlotContext.Provider value={value}>
      {children}
    </PageNavSlotContext.Provider>
  );
};

export const usePageNavSlot = () => {
  const context = useContext(PageNavSlotContext);

  if (!context) {
    throw new Error("usePageNavSlot must be used within a PageNavSlotProvider");
  }

  return context;
};
